import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, FileText, Ticket, Users, X, MessageSquare, ClipboardList, Settings, BellRing, BellOff, Award, Calendar, Newspaper, LifeBuoy, CreditCard } from 'lucide-react';
import { useNotifications, UnifiedNotification, NotificationCategory } from '../hooks/useNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { AssociateData } from '../services/otpAuthService';
import { categoriaNotificaVisibile, eDipendente, sessioneDiDipendente } from '../utils/sezioniDipendente';

// Fallback per categorie non configurate (es. community v1 legacy)
const DEFAULT_CATEGORY_STYLE = {
  label: 'Altro',
  icon: <Bell className="w-4 h-4" />,
  color: 'text-gray-600 dark:text-gray-400',
  bgColor: 'bg-gray-100 dark:bg-gray-800/50'
};

const CATEGORY_CONFIG: Partial<Record<NotificationCategory, { label: string; icon: React.ReactNode; color: string; bgColor: string }>> = {
  svincolo_atti: {
    label: 'Svincolo Atti',
    icon: <FileText className="w-4 h-4" />,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30'
  },
  voucher: {
    label: 'Voucher Rimborso',
    icon: <Ticket className="w-4 h-4" />,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30'
  },
  community_v2: {
    label: 'Community',
    icon: <Users className="w-4 h-4" />,
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-900/30'
  },
  feedback: {
    label: 'Feedback',
    icon: <MessageSquare className="w-4 h-4" />,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/30'
  },
  supporto: {
    label: 'Supporto',
    icon: <LifeBuoy className="w-4 h-4" />,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30'
  },
  sondaggio: {
    label: 'Sondaggi',
    icon: <ClipboardList className="w-4 h-4" />,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/30'
  },
  rete_certificata: {
    label: 'Rete Certificata',
    icon: <Award className="w-4 h-4" />,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30'
  },
  eventi: {
    label: 'Eventi',
    icon: <Calendar className="w-4 h-4" />,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/30'
  },
  circolari: {
    label: 'Circolari',
    icon: <Newspaper className="w-4 h-4" />,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30'
  },
  tesseramento: {
    label: 'Tesseramento',
    icon: <CreditCard className="w-4 h-4" />,
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-900/30'
  },
  sistema: {
    label: 'Sistema',
    icon: <Settings className="w-4 h-4" />,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800/50'
  }
};

/** Dati dell'associato salvati nel browser (null se mancano o sono illeggibili). */
function datiAssociato(): AssociateData | null {
  try {
    const dati = window.localStorage.getItem('associate_data');
    return dati ? JSON.parse(dati) : null;
  } catch (e) {
    return null;
  }
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}min fa`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h fa`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}g fa`;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

const GlobalNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<NotificationCategory | 'all'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    categoryCount,
    loading,
    hasMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore
  } = useNotifications();

  const {
    permission: pushPermission,
    isSubscribed: pushSubscribed,
    loading: pushLoading,
    isSupported: pushSupported,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe
  } = usePushNotifications();

  // Dipendente dello studio (piano 3): filtri solo delle categorie delle sue sezioni, niente «Elimina»
  // (toglierebbe la notifica a tutto lo studio: solo il titolare). Letto a ogni disegno.
  const dipendente = sessioneDiDipendente();
  const dati = datiAssociato();
  const categorieFiltro = (Object.keys(CATEGORY_CONFIG) as NotificationCategory[])
    .filter(cat => !dipendente || (eDipendente(dati) && categoriaNotificaVisibile(dati, cat)));

  const handlePushToggle = async () => {
    if (pushSubscribed) {
      await pushUnsubscribe();
    } else {
      await pushSubscribe();
    }
  };

  // Chiudi dropdown cliccando fuori
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredNotifications = activeFilter === 'all'
    ? notifications
    : notifications.filter(n => n.category === activeFilter);

  const handleNotificationClick = (notification: UnifiedNotification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bottone Campanella */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        title="Notifiche"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <>
            {/* Anello ping animato dietro al badge */}
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-400 animate-ping opacity-75" />
            {/* Badge sempre visibile con numero */}
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-[380px] max-h-[80vh] sm:max-h-[520px] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-[60]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Notifiche</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead()}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Segna tutte come lette"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filtri Categoria – flex-wrap per adattarsi a qualsiasi numero di categorie */}
          <div className="flex items-center flex-wrap gap-0.5 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            {/* Tutte – testo breve */}
            <button
              onClick={() => setActiveFilter('all')}
              className={`flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                activeFilter === 'all'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Tutte {unreadCount > 0 && `(${unreadCount})`}
            </button>
            {/* Separatore */}
            <span className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-0.5 flex-shrink-0" />
            {/* Categorie – solo icona + eventuale contatore, tooltip con il nome */}
            {categorieFiltro.map(cat => {
              const count = categoryCount[cat] || 0;
              const config = CATEGORY_CONFIG[cat]!;
              return (
                <button
                  key={cat}
                  title={`${config.label}${count > 0 ? ` (${count})` : ''}`}
                  onClick={() => setActiveFilter(cat)}
                  className={`relative flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                    activeFilter === cat
                      ? `${config.bgColor} ${config.color}`
                      : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                >
                  {config.icon}
                  {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 text-[9px] font-bold leading-[14px] text-white bg-red-500 rounded-full text-center">
                      {count > 9 ? '9+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Lista Notifiche */}
          <div className="overflow-y-auto max-h-[380px]">
            {filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Bell className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {activeFilter === 'all' ? 'Nessuna notifica' : `Nessuna notifica in ${(CATEGORY_CONFIG[activeFilter] || DEFAULT_CATEGORY_STYLE).label}`}
                </p>
              </div>
            ) : (
              <>
                {filteredNotifications.map(notification => {
                  const config = CATEGORY_CONFIG[notification.category] || DEFAULT_CATEGORY_STYLE;
                  return (
                    <div
                      key={notification._id}
                      className={`group flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 dark:border-gray-800/50 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                        !notification.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {/* Icona Categoria */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${config.bgColor} ${config.color}`}>
                        {config.icon}
                      </div>

                      {/* Contenuto */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug truncate ${!notification.isRead ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500"></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-4" title={notification.message}>
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-medium ${config.color}`}>
                            {config.label}
                          </span>
                          <span className="text-[10px] text-gray-400">•</span>
                          <span className="text-[10px] text-gray-400">{timeAgo(notification.createdAt)}</span>
                        </div>
                      </div>

                      {/* Azioni (visibili su hover) */}
                      <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!notification.isRead && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(notification._id); }}
                            className="p-1 text-gray-400 hover:text-blue-500 rounded transition-colors"
                            title="Segna come letta"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!dipendente && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotification(notification._id); }}
                            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Carica altro */}
                {hasMore && (
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full py-3 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Caricamento...' : 'Carica altre notifiche'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Footer - Toggle Push Notifications */}
          {pushSupported && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
              <div className="flex items-center gap-2">
                {pushSubscribed ? (
                  <BellRing className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <BellOff className="w-3.5 h-3.5 text-gray-400" />
                )}
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  Notifiche browser
                </span>
                {pushPermission === 'denied' && (
                  <span className="text-[10px] text-red-500">(bloccate)</span>
                )}
              </div>
              <button
                onClick={handlePushToggle}
                disabled={pushLoading || pushPermission === 'denied'}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                  pushSubscribed ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                title={pushPermission === 'denied'
                  ? 'Notifiche bloccate dal browser - riattivale nelle impostazioni del sito'
                  : pushSubscribed ? 'Disattiva notifiche browser' : 'Attiva notifiche browser'}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                    pushSubscribed ? 'translate-x-[18px]' : 'translate-x-[3px]'
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalNotificationBell;
